import { useCustomFields } from 'core/contexts/custom-fields/use-custom-fields.hook';
import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useTranslation } from 'core/i18n/i18n';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { parseRestErrorMessage } from 'core/utils/parse-rest-error.util';
import { settingsModulePaths } from 'core/routes/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    createActiveDirectory,
    getActiveDirectoryById,
    updateActiveDirectoryById,
} from '../service/active-directory.service';
import { ActiveDirectoryDetailsItem, ActiveDirectoryMapping } from '../components';
import { MASKED_PASSWORD_PLACEHOLDER } from '../settings-integrations-active-directory.constants';
import { ActiveDirectoryDetailsFormState, TestConnectionStatus } from '../types/active-directory.type';
import { useActiveDirectoryMappings } from './use-active-directory-mappings.hook';
import { useActiveDirectoryTestConnection } from './use-active-directory-test-connection.hook';

const mapActiveDirectoryDetailsItemToFormState = (
    activeDirectory?: ActiveDirectoryDetailsItem
): ActiveDirectoryDetailsFormState => ({
    name: activeDirectory?.name || '',
    url: activeDirectory?.url || '',
    group: activeDirectory?.group || '',
    interval: activeDirectory?.interval || '',
    username: activeDirectory?.username || '',
    userGroup: activeDirectory?.userGroup || '',
    password: activeDirectory?.hasSavedPassword ? MASKED_PASSWORD_PLACEHOLDER : '',
    mappings: activeDirectory?.mappings ?? [],
});

interface UseActiveDirectoryFormProps {
    activeDirectoryId?: string;
}

export const useActiveDirectoryForm = ({ activeDirectoryId }: UseActiveDirectoryFormProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isEditMode = activeDirectoryId != null && activeDirectoryId !== 'new';

    const { operatorsCustomFields } = useCustomFields();

    const [loading, setLoading] = useState<boolean>(isEditMode);
    const [activeDirectory, setActiveDirectory] = useState<ActiveDirectoryDetailsItem>();

    const [initialFormState, setInitialFormState] = useState<ActiveDirectoryDetailsFormState>(
        mapActiveDirectoryDetailsItemToFormState()
    );
    const [formState, setFormState] = useState<ActiveDirectoryDetailsFormState>(
        mapActiveDirectoryDetailsItemToFormState()
    );

    const isDirty = useMemo(
        () => JSON.stringify(formState) !== JSON.stringify(initialFormState),
        [formState, initialFormState]
    );

    const {
        testConnectionState,
        testConnectionDataPoints,
        fieldErrors,
        handleTestConnection,
        handleResetTestConnectionState,
        clearFieldError,
    } = useActiveDirectoryTestConnection({ activeDirectoryId: isEditMode ? activeDirectoryId : undefined, formState });

    const handleChangeField = useCallback(
        (field: string, value: string) => {
            setFormState((prev) => ({ ...prev, [field]: value }));
            clearFieldError(field);
        },
        [clearFieldError]
    );

    const identityProviderAttributes = useMemo(() => {
        if (isEditMode) {
            return testConnectionState.status === TestConnectionStatus.SUCCESS
                ? testConnectionDataPoints
                : (activeDirectory?.identityProviderAttributes ?? []);
        }
        return testConnectionDataPoints;
    }, [testConnectionDataPoints, testConnectionState, isEditMode, activeDirectory]);

    const handleInitializeMappings = useCallback((mappings: ActiveDirectoryMapping[]) => {
        setFormState((prev) => ({ ...prev, mappings }));
        setInitialFormState((prev) => ({ ...prev, mappings }));
    }, []);

    const { pocFields } = useActiveDirectoryMappings({
        activeDirectoryMappings: activeDirectory?.mappings,
        onMappingsReady: handleInitializeMappings,
    });

    const handleChange = useCallback(
        (field: string, value: string | ActiveDirectoryMapping[]) => {
            if (field === 'identityProviderMapping') {
                setFormState((prev) => ({ ...prev, mappings: value as ActiveDirectoryMapping[] }));
            } else {
                handleChangeField(field, value as string);
            }
        },
        [handleChangeField]
    );

    const handleOnClose = useCallback(() => {
        navigate(settingsModulePaths.activeDirectoryList);
    }, [navigate]);

    const unsavedData = useCheckUnsavedChanges(handleOnClose);

    useEffect(() => {
        unsavedData.setIsUnsavedChanges(isDirty);
    }, [isDirty, unsavedData]);

    const handleClose = useCallback(() => {
        unsavedData.showUnsavedChangesIfNeededOrClose();
    }, [unsavedData]);

    const handleEditConnectionDetails = useCallback(() => {
        handleResetTestConnectionState();

        if (!isDirty) {
            setFormState((prev) => ({ ...prev, password: '' }));
            setInitialFormState((prev) => ({ ...prev, password: '' }));
        }
    }, [handleResetTestConnectionState, isDirty]);

    const handleSave = useCallback(
        (isSaveAndExit?: boolean) => {
            const { mappings, ...connectionDetails } = formState;
            const payload = isSaveAndExit ? { ...connectionDetails, mappings: [] } : formState;

            const getNotificationKey = (type: 'success' | 'error') => {
                if (isEditMode) {
                    return `settings.integrations.active-directory.notification.update.${type}`;
                }
                if (isSaveAndExit) {
                    return `settings.integrations.active-directory.notification.saveAndExit.${type}`;
                }
                return `settings.integrations.active-directory.notification.create.${type}`;
            };

            const saveAction = isEditMode
                ? updateActiveDirectoryById(activeDirectoryId, payload, identityProviderAttributes)
                : createActiveDirectory(payload, identityProviderAttributes);

            saveAction
                .then(() => {
                    unsavedData.setIsUnsavedChanges(false);
                    showSuccessNotification(t(getNotificationKey('success')));
                    navigate(settingsModulePaths.activeDirectoryList);
                })
                .catch((error) => {
                    const defaultErrorMessage = t(
                        'settings.integrations.active-directory.notification.error.default-reason'
                    );
                    const errorMessage = parseRestErrorMessage(error, defaultErrorMessage);

                    showErrorNotification(
                        t(getNotificationKey('error'), {
                            errorMessage,
                        })
                    );
                });
        },
        [activeDirectoryId, identityProviderAttributes, formState, isEditMode, navigate, unsavedData, t]
    );

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        getActiveDirectoryById(activeDirectoryId)
            .then((item) => {
                const itemMappedToFormState = mapActiveDirectoryDetailsItemToFormState(item);
                setActiveDirectory(item);
                setFormState(itemMappedToFormState);
                setInitialFormState(itemMappedToFormState);
            })
            .finally(() => setLoading(false));
    }, [activeDirectoryId, isEditMode]);

    return {
        activeDirectory,
        formState,
        operatorsCustomFields,
        loading,
        isDirty,
        isEditMode,
        testConnectionState,
        identityProviderAttributes,
        pocFields,
        fieldErrors,
        handleChange,
        handleTestConnection,
        handleEditConnectionDetails,
        handleSave,
        handleClose,
    };
};
